import _ from 'lodash'
import * as Sentry from '@sentry/vue'
import {
  getObjectFromIndexedDB,
  getAllImageFromIndexedDB,
  Stores,
  setObjectToIndexedDB,
  removeObjectFromIndexedDB,
} from '@/utils/indexedDB/indexed.db'

export const dataURLToBlob = (dataURL) => {
  const BASE64_MARKER = ';base64,'
  if (dataURL.indexOf(BASE64_MARKER) == -1) {
    const parts = dataURL.split(',')
    const contentType = parts[0].split(':')[1]
    const raw = parts[1]

    return new Blob([raw], { type: contentType })
  }

  const parts = dataURL.split(BASE64_MARKER)
  const contentType = parts[0].split(':')[1]
  const raw = window.atob(parts[1])
  const rawLength = raw.length

  const uInt8Array = new Uint8Array(rawLength)

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i)
  }

  return new Blob([uInt8Array], { type: contentType })
}

export async function handleLoadBackgroundCache(slides) {
  try {
    const slideIds = slides.map((item) => item.id)
    const getBackgroundImageCachePromises = slideIds.map((slideId) => {
      return getObjectFromIndexedDB(slideId, Stores.backgroundImageStore)
    })

    const backgroundImageCaches = await Promise.all(getBackgroundImageCachePromises)

    slides.forEach((slide, index) => {
      slide.backgroundCache = backgroundImageCaches[index] ?
        URL.createObjectURL(backgroundImageCaches[index].blob) :
        null
      slide.backgroundCacheExtension = backgroundImageCaches[index] ?
        backgroundImageCaches[index].extension :
        null
      slide.cropperDataCache = backgroundImageCaches[index] ?
        backgroundImageCaches[index].cropperDataCache :
        null
      if (backgroundImageCaches[index] && backgroundImageCaches[index].originBackgroundCache) {
        slide.originBackgroundCache = backgroundImageCaches[index].originBackgroundCache
      }
    })
  } catch (e) {
    Sentry.captureException(e)
  }
}

export async function handleLoadQuestionImageCache(slides) {
  try {
    const slideIds = slides.map((item) => item.id)

    const getQuestionImageCachePromises = slideIds.map((slideId) => {
      return getObjectFromIndexedDB(slideId, 'question_images')
    })
    const questionImageCaches = await Promise.all(getQuestionImageCachePromises)

    slides.forEach((slide, index) => {
      // question image
      const imageCacheUrl = questionImageCaches[index] ?
        URL.createObjectURL(questionImageCaches[index].blob) :
        null
      if (imageCacheUrl) {
        slide.image = {
          thumbnail: imageCacheUrl,
          large: imageCacheUrl,
          xLarge: imageCacheUrl,
        }
      }
      slide.questionImageExtension = questionImageCaches[index] ?
        questionImageCaches[index].extension :
        null
      slide.questionImageCropperDataCache = questionImageCaches[index] ?
        questionImageCaches[index].questionImageCropperData :
        null
      slide.originQuestionImageCache = questionImageCaches[index] ?
        questionImageCaches[index].originQuestionImage :
        null
    })
  } catch (e) {
    Sentry.captureException(e)
  }
}

export async function applyImageDataToSlideOption(slideOption, imageData) {
  if (!imageData) return

  if (imageData.imageCropped) {
    slideOption.imageCropped = URL.createObjectURL(imageData.blob)
  }
  if (imageData.pairImageCropped) {
    slideOption.pairImageCropped = URL.createObjectURL(imageData.pairBlob)
  }
  if (imageData.originImage) {
    slideOption.image = imageData.originImage
  }
  if (imageData.pairOriginImage) {
    slideOption.pairImage = imageData.pairOriginImage
  }
  if (imageData.lastCropperData) {
    slideOption.lastCropperData = imageData.lastCropperData
  }
  if (imageData.pairImageCropperData) {
    slideOption.pairImageCropperData = imageData.pairImageCropperData
  }
}

export async function handleLoadSlideOptionImageCache(slides) {
  try {
    const allSlideOptionImage = await getAllImageFromIndexedDB('slide_option_images')
    const slideOptionImageMap = _.keyBy(allSlideOptionImage, 'id')
    slides.forEach((slide) => {
      // slide-option images
      if (slide.SlideOptions && slide.SlideOptions.length > 0) {
        slide.SlideOptions.forEach((slideOption) => {
          const imageData = slideOptionImageMap[slideOption.id]
          applyImageDataToSlideOption(slideOption, imageData)
        })
      }
    })
  } catch (e) {
    Sentry.captureException(e)
  }
}

export async function copyBackgroundCacheToSlides(originSlide, targetSlides) {
  if (!originSlide || !originSlide.backgroundCache ||
    !targetSlides || targetSlides.length === 0
  ) {
    return
  }
  targetSlides.forEach((slide) => {
    slide.backgroundCache = originSlide.backgroundCache
    slide.cropperDataCache = originSlide.cropperDataCache
    slide.originBackgroundCache = originSlide.originBackgroundCache
    slide.backgroundCacheExtension = originSlide.backgroundCacheExtension
  })

  const blob = await fetch(originSlide.backgroundCache).then((r) => r.blob())
  targetSlides.forEach((slide) => {
    setObjectToIndexedDB(slide.id, Stores.backgroundImageStore, {
      blob,
      cropperDataCache: originSlide.cropperDataCache,
      originBackgroundCache: originSlide.originBackgroundCache,
      extension: originSlide.backgroundCacheExtension,
    })
  })
}

export async function copyQuestionImageCacheToSlides(originSlide, targetSlides) {
  if (!originSlide || !originSlide.image || !targetSlides || targetSlides.length === 0) return

  targetSlides.forEach((slide) => {
    slide.image = originSlide.image
    slide.questionImageCropperData = originSlide.questionImageCropperData
    slide.originQuestionImage = originSlide.originQuestionImage
    slide.questionImageExtension = originSlide.questionImageExtension
  })

  const blob = await fetch(originSlide.image.large).then((r) => r.blob())
  targetSlides.forEach((slide) => {
    setObjectToIndexedDB(slide.id, Stores.questionImageStore, {
      blob,
      questionImageCropperData: originSlide.questionImageCropperData,
      originQuestionImage: originSlide.originQuestionImage,
      extension: originSlide.questionImageExtension,
    })
  })
}

export async function copySlideOptionImageCache(originSlideOption, targetSlideOption) {
  if (!originSlideOption || !targetSlideOption || !originSlideOption.imageCropped) return
  targetSlideOption.image = originSlideOption.image
  targetSlideOption.imageCropped = originSlideOption.imageCropped
  targetSlideOption.lastCropperData = originSlideOption.lastCropperData
  const blob = await fetch(originSlideOption.imageCropped).then((r) => r.blob())
  setObjectToIndexedDB(targetSlideOption.id, Stores.slideOptionImageStore, {
    blob,
    lastCropperData: originSlideOption.lastCropperData,
    originImage: originSlideOption.image,
  })
}

export function resetBackgroundCacheToSlides(slides) {
  if (!slides || slides.length === 0) return
  slides.forEach((slide) => {
    slide.backgroundCache = null
    slide.cropperDataCache = null
    slide.originBackgroundCache = null
    slide.backgroundCacheExtension = null
    removeObjectFromIndexedDB(slide.id, Stores.backgroundImageStore)
  })
}

export async function handleLoadAllImageCache(slides) {
  await handleLoadBackgroundCache(slides)
  await handleLoadQuestionImageCache(slides)
  await handleLoadSlideOptionImageCache(slides)
}
